import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getTestById, updateTestStatus } from "../api";
import "./ViewQuiz.css";

const ViewQuiz = () => {
  const { id } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTestById(id);
        setQuiz(data);
      } catch (err) {
        console.error("Error fetching quiz:", err);
        setError("Failed to load quiz details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [id]);

  const handleChangeStatus = async (status) => {
    if (!quiz) return;
    if (!window.confirm(`Are you sure you want to mark this quiz as "${status}"?`)) return;

    try {
      setUpdatingStatus(true);
      let payload = {};
      if (status === "public") payload = { is_published: true, is_archived: false };
      else if (status === "draft") payload = { is_published: false, is_archived: false };
      else if (status === "archive") payload = { is_archived: true };

      await updateTestStatus(quiz.id, payload);
      setQuiz((prev) => ({ ...prev, ...payload }));
      setToastMessage(`Quiz status updated to "${status}"!`);
      setTimeout(() => setToastMessage(""), 3000);
    } catch (err) {
      console.error("Failed to update quiz status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = () => {
    if (quiz?.is_archived) return "red";
    if (quiz?.is_published) return "green";
    return "orange";
  };

  if (loading) return <p className="loading-message">Loading quiz details...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!quiz) return <p className="error-message">Quiz not found.</p>;

  return (
    <div className="view-quiz-page">
      {/* Toast Notification */}
      {toastMessage && <div className="toast">{toastMessage}</div>}

      {/* Header */}
      <div className="quiz-header-card">
        <img
          src={quiz.course?.image_url || "/fallback.jpg"}
          alt={quiz.course?.title || "Quiz Thumbnail"}
          className="quiz-thumbnail"
          onError={(e) => (e.target.src = "/fallback.jpg")}
        />
        <div className="quiz-info">
          <h1>{quiz.title || "Untitled Quiz"}</h1>
          <p className="quiz-description">{quiz.description || "No description provided."}</p>
          <div className="course-meta">
            <strong>Course:</strong> {quiz.course?.title || "N/A"}
          </div>
        </div>
      </div>

      {/* Status Buttons */}
      <div className="status-buttons">
        <button
          className={`btn-status ${quiz.is_published && !quiz.is_archived ? "active green" : "inactive green"}`}
          onClick={() => handleChangeStatus("public")}
          disabled={updatingStatus}
        >
          Public
        </button>
        <button
          className={`btn-status ${!quiz.is_published && !quiz.is_archived ? "active orange" : "inactive orange"}`}
          onClick={() => handleChangeStatus("draft")}
          disabled={updatingStatus}
        >
          Draft
        </button>
        <button
          className={`btn-status ${quiz.is_archived ? "active red" : "inactive red"}`}
          onClick={() => handleChangeStatus("archive")}
          disabled={updatingStatus}
        >
          Archive
        </button>
      </div>

      {/* Stats Section */}
      <div className="quiz-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📢</div>
          <div className="stat-label">Status</div>
          <div className="stat-value" style={{ color: getStatusColor() }}>
            {quiz.is_archived ? "Archived" : quiz.is_published ? "Published" : "Draft"}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-label">Attempts</div>
          <div className="stat-value">{quiz.attempts || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-label">Average Score</div>
          <div className="stat-value">{quiz.avg_score ? `${quiz.avg_score}%` : "N/A"}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-label">Created At</div>
          <div className="stat-value">
            {quiz.created_at ? new Date(quiz.created_at).toLocaleDateString() : "N/A"}
          </div>
        </div>
      </div>

      {/* Questions Section */}
      <div className="questions-section">
        <h2 className="section-title">Questions</h2>
        {!quiz.questions || quiz.questions.length === 0 ? (
  <p>No questions found for this quiz.</p>
) : (
  <div className="questions-list">
    {quiz.questions.map((q, idx) => {
      const isOpen = expanded === idx;
      const options = Array.isArray(q.options) ? q.options : [];

      return (
        <div key={q.id || idx} className="question-card">
          <div
            className="question-header"
            onClick={() => setExpanded(isOpen ? null : idx)}
          >
            <p>
              <strong>Q{idx + 1}:</strong> {q.body || "No question text"}
            </p>
            <span className="toggle-icon">{isOpen ? "▲" : "▼"}</span>
          </div>

          {isOpen && (
            <ul className="options-list">
              {options.length === 0 ? (
                <li className="no-options">No options provided.</li>
              ) : (
                options.map((opt) => (
                  <li
                    key={opt.id || `${idx}-${opt.body}`}
                    className={`option ${opt.is_correct ? "correct" : ""}`}
                  >
                    {opt.body} {opt.is_correct && <span>✅</span>}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      );
    })}
  </div>
)}
      </div>

      {/* Back Button */}
      <div className="back-button">
        <Link to="/quizzes" className="btn-primary">
          ← Back to Quizzes
        </Link>
      </div>
    </div>
  );
};

export default ViewQuiz;
